import { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Settings, Building2, Upload, Save, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClubSettings = () => {
  const { club, refreshClub } = useOutletContext();
  const { clubId } = useParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    logo_url: ''
  });

  useEffect(() => {
    if (club) {
      setFormData({
        name: club.name || '',
        description: club.description || '',
        contact_email: club.contact_email || '',
        contact_phone: club.contact_phone || '',
        logo_url: club.logo_url || ''
      });
    }
  }, [club]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const token = localStorage.getItem('club_token');
    try {
      await axios.put(`${API}/clubs/my-club`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully!');
      if (refreshClub) refreshClub();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="club-settings">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl uppercase">
          Club <span className="text-emerald-400">Settings</span>
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Manage your club information
        </p>
      </div>

      <form onSubmit={handleSave} className="max-w-2xl space-y-6">
        {/* Club Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/40 rounded-sm p-6"
        >
          <h2 className="font-heading text-lg uppercase mb-6 flex items-center gap-2">
            <Building2 className="text-emerald-400" size={18} />
            Club Information
          </h2>

          <div className="space-y-5">
            {/* Logo Preview */}
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-sm flex items-center justify-center overflow-hidden">
                {formData.logo_url ? (
                  <img src={formData.logo_url} alt="Club logo" className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="text-emerald-400" size={32} />
                )}
              </div>
              <div className="flex-1">
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Logo URL
                </label>
                <Input
                  value={formData.logo_url}
                  onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                  className="bg-transparent border-border focus:border-emerald-500"
                  placeholder="https://..."
                />
              </div>
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                Club Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="bg-transparent border-border focus:border-emerald-500"
                placeholder="Your club name"
              />
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                Description
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-transparent border-border focus:border-emerald-500 min-h-[100px]"
                placeholder="About your club..."
              />
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                Primary Sport
              </label>
              <Input
                value="Soccer"
                disabled
                className="bg-transparent border-border opacity-60"
              />
              <p className="text-xs text-muted-foreground mt-1">Sport type is locked to Soccer.</p>
            </div>
          </div>
        </motion.div>

        {/* Contact Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border border-border/40 rounded-sm p-6"
        >
          <h2 className="font-heading text-lg uppercase mb-6 flex items-center gap-2">
            <Mail className="text-emerald-400" size={18} />
            Contact Information
          </h2>

          <div className="space-y-5">
            <div>
              <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                Contact Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  className="bg-transparent border-border pl-10 focus:border-emerald-500"
                  placeholder="contact@yourclub.com"
                />
              </div>
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                Contact Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  className="bg-transparent border-border pl-10 focus:border-emerald-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Subscription Info (Read Only) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card border border-border/40 rounded-sm p-6"
        >
          <h2 className="font-heading text-lg uppercase mb-6 flex items-center gap-2">
            <Settings className="text-emerald-400" size={18} />
            Subscription
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-card/50 border border-border/30 rounded-sm p-4">
              <p className="text-xs text-muted-foreground uppercase mb-1">Status</p>
              <p className={`font-semibold ${
                club?.subscription_status === 'active' ? 'text-emerald-400' : 'text-yellow-400'
              }`}>
                {club?.subscription_status === 'active' ? 'Active' : 'Trial'}
              </p>
            </div>
            <div className="bg-card/50 border border-border/30 rounded-sm p-4">
              <p className="text-xs text-muted-foreground uppercase mb-1">Teams Limit</p>
              <p className="font-semibold">{club?.teams_limit || 2} teams</p>
            </div>
          </div>

          {club?.subscription_status !== 'active' && (
            <div className="mt-4 bg-emerald-500/10 border border-emerald-500/30 rounded-sm p-4">
              <p className="text-sm text-emerald-400 font-semibold mb-1">Trial Period</p>
              <p className="text-xs text-muted-foreground">
                Your trial allows up to 2 teams. Upgrade to add more teams and unlock premium features.
              </p>
            </div>
          )}
        </motion.div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            type="submit" 
            disabled={loading}
            className="bg-emerald-500 text-black hover:bg-emerald-400"
          >
            {loading ? (
              <>Saving...</>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Future Features Note */}
      <div className="max-w-2xl bg-card border border-border/40 rounded-sm p-4 opacity-60">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold">Coming Soon:</span> White-label branding, custom domain, and advanced subscription options.
        </p>
      </div>
    </div>
  );
};

export default ClubSettings;
